import { Entitlements } from '@protector/entities/licensing/constants';
import { useHasWorkspaceEntitlement } from '@protector/entities/licensing/queries';
import { selectCurrentUser } from 'core/services/auth/auth.reducer';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { getAvailableWorkspaces, getSelectedCompany } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

const joinUniqueValues = (values: (string | undefined)[]): string =>
  [...new Set(values.filter((value): value is string => !!value))].join(', ');

const useFirstLoad = () => {
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  const { pathname } = useLocation();

  const screen = pathname.split('/').pop();
  const isWorkspaceListScreen = screen === 'hall-of-companies';
  const { settled, hasEntitlement } = useHasWorkspaceEntitlement(Entitlements.PROTECTOR_RTR, isWorkspaceListScreen);

  const company = useSelector(getSelectedCompany);
  const property = useSelector(getSelectedProperty);
  const user = useSelector(selectCurrentUser);
  const availableWorkspaces = useSelector(getAvailableWorkspaces);

  const segmentTracking = useSegmentTracking();

  useEffect(() => {
    const isHallOfProperties = screen === company?.id;
    const screenLoadsWorkspaces = isWorkspaceListScreen || isHallOfProperties;

    if (
      !firstLoad ||
      !user ||
      (isWorkspaceListScreen && !settled) ||
      (screenLoadsWorkspaces && !availableWorkspaces.length) ||
      (screen !== company?.id && screen !== property?.id && screen !== 'hall-of-companies' && (!company || !property))
    )
      return;

    let screenToTrack = screen;

    if (isHallOfProperties) {
      screenToTrack = 'Hall of Properties';
    } else if (screen === property?.id) {
      screenToTrack = 'Property Dashboard';
    }

    const workspaceName = joinUniqueValues(availableWorkspaces.map(({ name }) => name));
    const workspaceId = joinUniqueValues(availableWorkspaces.map(({ id }) => id));

    segmentTracking.track(`Loaded Panel`, {
      screen: screenToTrack,
      ...(isWorkspaceListScreen && { 'protector.rtr': hasEntitlement }),
      ...(workspaceName && { 'Workspace Name': workspaceName }),
      ...(workspaceId && { 'Workspace ID': workspaceId })
    });

    setFirstLoad(false);
  }, [company, firstLoad, property, screen, segmentTracking, user, availableWorkspaces, settled, hasEntitlement, isWorkspaceListScreen]);
};

export default useFirstLoad;
